import {useContext, useEffect, useState} from "react";
import {useRecoilValue} from "recoil";
import axios from "axios";
import {bookingDataValueState} from "../../state";
import {GlobalContext} from "../../../../../util/state/GlobalContextProvider";
import {IAncillaryDiscountRange, resolveGovernanceRange} from "./discount";

/**
 * Hook che recupera il range sconto (min/max) dalla governance desk-discount
 * per la location, dealer, legalEntity della RA corrente e i profili dell'operatore loggato.
 *
 * Chiama GET /pricing/desk-discount-governance/paged/filter con i parametri dello scope
 * e filtra lato client il record attivo con validità corrente.
 * Se l'utente ha più profili, prende il range più ampio (min più basso, max più alto).
 *
 * Restituisce null se non trova nessuna configurazione applicabile (→ il componente
 * AncillaryDiscountSelect userà il fallback documentato).
 *
 * US-NTWRA-001 (LPP-22289), RF-05.
 */
const useDeskDiscountRange = (): IAncillaryDiscountRange | null => {
	const bookingDataValue = useRecoilValue(bookingDataValueState);
	const {globalData} = useContext(GlobalContext);
	const [range, setRange] = useState<IAncillaryDiscountRange | null>(null);

	useEffect(() => {
		const fetchGovernance = async () => {
			try {
				const locationCode = bookingDataValue?.bookingDetail?.pickUpLocation?.code;
				const dealer = bookingDataValue?.dealer;
				const legalEntity = bookingDataValue?.idTenant;
				const profiles: any[] = globalData?.user?.profiles;

				if (!locationCode || !dealer || !legalEntity || !profiles?.length) {
					setRange(null);
					return;
				}

				const lppProfileIds = profiles.map((p: any) => p.id).filter(Boolean);

				const params = new URLSearchParams();
				params.append("locationCodes", locationCode);
				params.append("dealers", dealer);
				params.append("legalEntities", legalEntity);
				lppProfileIds.forEach((id: number) => params.append("lppProfileIds", String(id)));
				params.append("size", "500");
				params.append("page", "0");

				const response = await axios.get(
					`/pricing/desk-discount-governance/paged/filter?${params.toString()}`
				);

				const records = response.data?.content ?? [];
				const now = new Date().toISOString();

				// Filtra record attivi con validità corrente
				const activeRecords = records.filter((r: any) =>
					r.active === true &&
					r.validityDateFrom && r.validityDateFrom <= now &&
					(!r.validityDateTo || r.validityDateTo >= now)
				);

				if (activeRecords.length === 0) {
					setRange(null);
					return;
				}

				// Se più profili matchano, prendi il range più ampio (min più basso, max più alto)
				// discountMin/discountMax sono in centesimi di punto (es. 1000 = 10%)
				const minValue = Math.min(...activeRecords.map((r: any) => r.discountMin));
				const maxValue = Math.max(...activeRecords.map((r: any) => r.discountMax));

				// LPP-24845 (requisito aggiornato): passo 5. Conversione centesimi -> punti percentuali
				// tramite la stessa funzione usata dal post-firma (useDeskDiscountRangeForRa), che offre
				// SOLO i multipli di 5 interni al range (min arrotondato in su, max in giu' al multiplo di
				// 5). PRC valida in modo inclusivo sui soli estremi: i valori interni al range sono ammessi
				// e l'arrotondamento verso l'interno evita l'overshoot (400 DISCOUNT_OUT_OF_RANGE). La
				// configurazione governance (min/max) e' a sua volta vincolata a passo 5.
				// Restituisce null quando nessun multiplo di 5 e' ammissibile -> fallback documentato.
				setRange(resolveGovernanceRange(minValue, maxValue));
			} catch (e) {
				// Se l'endpoint fallisce, il componente userà il fallback
				setRange(null);
			}
		};

		fetchGovernance();
	}, [
		bookingDataValue?.bookingDetail?.pickUpLocation?.code,
		bookingDataValue?.dealer,
		bookingDataValue?.idTenant,
		globalData?.user?.profiles
	]);

	return range;
};

export default useDeskDiscountRange;
